import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { HpButton, HpCardHeader, IconButton } from '@hp/ui';
import { api } from '../../api/client';
import { useCan } from '../../api/me';
import { trpcClient, trpc } from '../../api/trpc';
import { ErrorAlert } from '../../components/error-alert';
import { invalidateProjects, projectFileUrl, type ProjectPhoto } from './queries';

export function PhotoGallery({ projectId, photos }: { projectId: string; photos: ProjectPhoto[] }) {
  const { t } = useTranslation(['projects', 'common']);
  const fileInput = useRef<HTMLInputElement>(null);
  const canEdit = useCan()('projects.edit');

  const upload = useMutation({
    mutationFn: (files: FileList) => {
      const form = new FormData();
      for (const file of Array.from(files)) form.append('photos', file);
      return api.upload<{ photos: ProjectPhoto[] }>(`/api/projects/${projectId}/photos`, form);
    },
    onSuccess: invalidateProjects,
  });

  const update = useMutation(trpc.projects.photos.update.mutationOptions({ onSuccess: invalidateProjects }));

  const move = useMutation({
    mutationFn: async ({ index, dir }: { index: number; dir: -1 | 1 }) => {
      const a = photos[index];
      const b = photos[index + dir];
      if (!a || !b) return;
      // Swap sort orders; the query refetch restores canonical order.
      await trpcClient.projects.photos.update.mutate({ projectId, photoId: a.id, patch: { sortOrder: b.sortOrder } });
      await trpcClient.projects.photos.update.mutate({ projectId, photoId: b.id, patch: { sortOrder: a.sortOrder } });
    },
    onSuccess: invalidateProjects,
  });

  const remove = useMutation(trpc.projects.photos.remove.mutationOptions({ onSuccess: invalidateProjects }));

  return (
    <section className="hp-card">
      <HpCardHeader title={t('photos.title')} subtitle={t('photos.subtitle')} icon="image">
        <HpButton
          variant="primary"
          icon="upload"
          busy={upload.isPending}
          disabled={!canEdit}
          onClick={() => fileInput.current?.click()}
        >
          {upload.isPending ? t('photos.uploading') : t('photos.upload')}
        </HpButton>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,image/avif"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) upload.mutate(e.target.files);
            e.target.value = '';
          }}
        />
      </HpCardHeader>

      <div className="hp-card__body bo-sections">
        <ErrorAlert error={upload.error ?? update.error ?? move.error ?? remove.error} />

        {photos.length === 0 ? (
          <p className="bo-muted">{t('photos.empty')}</p>
        ) : (
          <div className="bo-gallery">
            {photos.map((photo, i) => (
              <figure key={photo.id} className="bo-photo">
                <a href={projectFileUrl(photo.fileId)} target="_blank" rel="noreferrer" title={t('photos.openFull')}>
                  <img src={projectFileUrl(photo.fileId)} alt={photo.caption ?? ''} />
                </a>
                {i === 0 && <span className="bo-photo__cover">{t('photos.cover')}</span>}
                <figcaption>
                  <CaptionInput
                    key={`${photo.id}:${photo.caption ?? ''}`}
                    photo={photo}
                    onSave={(caption) => update.mutate({ projectId, photoId: photo.id, patch: { caption } })}
                  />
                  {canEdit && (
                    <span className="bo-row bo-row--between">
                      <span className="bo-row">
                        <IconButton
                          icon="chevronLeft"
                          aria-label={t('photos.moveEarlier')}
                          title={t('photos.moveEarlier')}
                          busy={move.isPending && move.variables?.index === i && move.variables.dir === -1}
                          disabled={i === 0 || move.isPending}
                          onClick={() => move.mutate({ index: i, dir: -1 })}
                        />
                        <IconButton
                          icon="chevronRight"
                          aria-label={t('photos.moveLater')}
                          title={t('photos.moveLater')}
                          busy={move.isPending && move.variables?.index === i && move.variables.dir === 1}
                          disabled={i === photos.length - 1 || move.isPending}
                          onClick={() => move.mutate({ index: i, dir: 1 })}
                        />
                      </span>
                      <IconButton
                        icon="trash"
                        className="bo-icon-danger"
                        aria-label={t('photos.delete')}
                        title={t('photos.delete')}
                        busy={remove.isPending && remove.variables?.photoId === photo.id}
                        disabled={remove.isPending}
                        onClick={() => {
                          if (window.confirm(t('photos.confirmDelete')))
                            remove.mutate({ projectId, photoId: photo.id });
                        }}
                      />
                    </span>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/** Local draft, committed on blur/Enter only when it actually changed. */
function CaptionInput({ photo, onSave }: { photo: ProjectPhoto; onSave: (caption: string | null) => void }) {
  const { t } = useTranslation('projects');
  const [value, setValue] = useState(photo.caption ?? '');

  function commit() {
    const trimmed = value.trim();
    if (trimmed !== (photo.caption ?? '')) onSave(trimmed === '' ? null : trimmed);
  }

  return (
    <input
      className="bo-photo__caption"
      placeholder={t('photos.captionPlaceholder')}
      aria-label={t('photos.captionAria')}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
